import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, Sparkles, LogOut, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/lib/brand";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/services" },
  { label: "Astrologers", to: "/astrologers" },
  { label: "Kundali", to: "/kundali" },
  { label: "Shop", to: "/shop" },
  { label: "Learn", to: "/learn" },
  { label: "Membership", to: "/membership" },
  { label: "Blogs", to: "/blogs" },
];

export default function Navbar() {
  const [open, setOpen] = React.useState(false);
  const loc = useLocation();
  const nav = useNavigate();
  const { user, logout } = useAuth();

  const isAuthed = user && user !== false;

  return (
    <header
      data-testid="site-nav"
      className="sticky top-0 z-50 backdrop-blur-2xl bg-black/70 border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3 group">
          <div className="relative w-11 h-11 flex-none flex items-center justify-center rounded-full bg-black border border-[#D4AF37]/40 shadow-[0_0_20px_rgba(212,175,55,0.15)] overflow-hidden">
            <img
              src={BRAND.logoUrl}
              alt="Vedacosmos"
              className="w-11 h-11 object-cover"
              loading="eager"
            />
          </div>
          <div className="leading-none">
            <div className="font-serif text-xl tracking-tight text-[#EAE6DF]">Vedacosmos</div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]/80">
              Ancient · Modern
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={`text-sm tracking-wide transition-colors duration-200 ${
                loc.pathname === n.to
                  ? "text-[#D4AF37]"
                  : "text-[#A0A4AB] hover:text-[#EAE6DF]"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {isAuthed ? (
            <>
              <button
                data-testid="nav-dashboard-btn"
                onClick={() => nav(user.role === "admin" ? "/admin" : (user.role === "astrologer" || user.role === "pandit") ? "/provider" : "/dashboard")}
                className="hidden sm:flex items-center gap-2 px-4 py-2 text-sm rounded-full border border-white/15 text-[#EAE6DF] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-200"
              >
                <User size={14} strokeWidth={1.5} /> {user.name?.split(" ")[0] || "Profile"}
              </button>
              <button
                data-testid="nav-logout-btn"
                onClick={async () => { await logout(); nav("/"); }}
                className="p-2 rounded-full border border-white/15 text-[#A0A4AB] hover:text-[#FF5A36] hover:border-[#FF5A36] transition-colors duration-200"
                aria-label="Logout"
              >
                <LogOut size={14} strokeWidth={1.5} />
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-login-btn"
                className="hidden sm:block px-4 py-2 text-sm text-[#EAE6DF] hover:text-[#D4AF37] transition-colors"
              >
                Login
              </Link>
              <Link
                to="/register"
                data-testid="nav-signup-btn"
                className="px-5 py-2 text-sm rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#E5C865] transition-colors duration-200"
              >
                Get Started
              </Link>
            </>
          )}
          <button
            className="lg:hidden p-2 text-[#EAE6DF]"
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            <Menu size={20} />
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl">
          <div className="px-6 py-4 flex flex-col gap-4">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className={`text-sm ${
                  loc.pathname === n.to ? "text-[#D4AF37]" : "text-[#A0A4AB]"
                }`}
              >
                {n.label}
              </Link>
            ))}
            {isAuthed && (
              <Link to="/dashboard" onClick={() => setOpen(false)} className="text-sm text-[#EAE6DF]">
                Dashboard
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
